import { useFlags } from 'launchdarkly-react-client-sdk';

import { useCustomAuth } from 'auth/AuthContext';
import useGetMyPermissions from 'hooks/queries/useGetMyPermissions';
import { AccessCentrePermissionKey } from 'models/enums';

import type { PermissionKey } from 'domains/accessCentre/types';
/**
 * Access Centre RBAC helpers. When LaunchDarkly `accessCenter` is off, all checks pass (legacy behavior).
 * When `accessCenter` is on, permission keys come from GET `/me` (`useGetMyPermissions`).
 */
export const useAccessCentrePermissions = () => {
    const { accessCenter: hasAccessCenterFlag } = useFlags();
    const { isAuthenticated } = useCustomAuth();

    const isGated = Boolean(hasAccessCenterFlag);

    const { data: meResponse, isLoading, isSuccess } = useGetMyPermissions({
        enabled: isAuthenticated && isGated,
    });

    const permissions: PermissionKey[] = isGated && isAuthenticated ? meResponse?.permissions ?? [] : [];

    /** True when Access Centre is on and GET /me succeeded with an empty `permissions` array. */
    const hasNoAccess = isGated && isAuthenticated && isSuccess && (meResponse?.permissions?.length ?? 0) === 0;

    const userHasPermissionKey = (key: PermissionKey) => permissions.includes(key);

    const allow = (key: PermissionKey) => !isGated || userHasPermissionKey(key);

    /** Contacts list: `contacts.view` and/or `contacts.view_phi` (PHI scope always depends on view in Access Centre). */
    const canAccessContacts =
        !isGated ||
        userHasPermissionKey(AccessCentrePermissionKey.ContactsView) ||
        userHasPermissionKey(AccessCentrePermissionKey.ContactsViewPhi);

    const hasContactsView = userHasPermissionKey(AccessCentrePermissionKey.ContactsView);
    const hasContactsViewPhi = userHasPermissionKey(AccessCentrePermissionKey.ContactsViewPhi);

    /**
     * PHI-bearing columns: when gated, require both `contacts.view` and `contacts.view_phi`.
     * With only `contacts.view`, non-PHI columns are shown. Ungated tenants keep legacy full visibility.
     */
    const canViewContactsFullPhi = !isGated || (hasContactsView && hasContactsViewPhi);

    return {
        isAccessCentrePermissionsLoading: isLoading,
        hasNoAccess,
        hasAccessCentrePermission: (key: PermissionKey) => allow(key),

        canViewAccessManagement: allow(AccessCentrePermissionKey.AccessManagementView),
        canManageAccessManagement: allow(AccessCentrePermissionKey.AccessManagementManage),

        canViewContent: allow(AccessCentrePermissionKey.ContentView),
        canManageContent: allow(AccessCentrePermissionKey.ContentManage),
        canPublishContent: allow(AccessCentrePermissionKey.ContentPublish),

        canAccessContacts,
        canViewContactsFullPhi,
        canUploadContacts: allow(AccessCentrePermissionKey.ContactsUpload),

        canViewAudiences: allow(AccessCentrePermissionKey.AudiencesView),
        canManageAudiences: allow(AccessCentrePermissionKey.AudiencesManage),
        canViewAudienceContacts: allow(AccessCentrePermissionKey.AudiencesViewContacts),
        canDownloadAudienceContacts: allow(AccessCentrePermissionKey.AudiencesDownloadContacts),

        canViewValue: allow(AccessCentrePermissionKey.ValueView),
        canCreateValue: allow(AccessCentrePermissionKey.ValueCreate),

        canViewCurations: allow(AccessCentrePermissionKey.CurationsView),
        canManageCurationsDrafts: allow(AccessCentrePermissionKey.CurationsManageDrafts),
        canPublishCurations: allow(AccessCentrePermissionKey.CurationsPublish),
        canCurationsAbTesting: allow(AccessCentrePermissionKey.CurationsAbTesting),
        canViewCurationsAuditLog: allow(AccessCentrePermissionKey.CurationsAuditLog),

        canViewInsightsDashboards: allow(AccessCentrePermissionKey.InsightsViewDashboards),
        canDownloadInsightsCsvs: allow(AccessCentrePermissionKey.InsightsDownloadCsvs),

        canRegisterAiBrandVoice: allow(AccessCentrePermissionKey.AiBrandRegistration),
    };
};
